import { useMemo } from 'react';
import { Controller } from 'react-hook-form';
import { UserIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AGE_OPTIONS, GENDER_OPTIONS, TONE_OF_VOICE_OPTIONS } from '@/features/audiences/model';
import { normalizeDashes } from '@/features/audiences/lib';
import { getCountryOptions } from '@/shared/utils/countries';
import {
  FormSectionV2,
  FormSubsectionV2,
  PersonaSelectField,
  PersonaTagField,
  PersonaTextField,
} from '../../PersonaFormSectionsV2';
import { isFieldRequired } from '../CreatePersonaForm.schema';
import { useCreatePersonaFormContext } from '../hooks/useCreatePersonaFormContext';

export function BasicInformationSectionV2() {
  const { t, i18n } = useTranslation('audiences');
  const { control } = useCreatePersonaFormContext();

  const countryOptions = useMemo(() => getCountryOptions(i18n.language), [i18n.language]);

  return (
    <FormSectionV2
      icon={<UserIcon className="size-5" />}
      title={t('audiences.basicInformationSection.basicInformation')}
      description={t('audiences.basicInformationSection.essentialDetailsAboutThePersona')}
    >
      <div className="grid grid-cols-[1fr_1fr_2fr] gap-sm">
        <Controller
          name="gender"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaSelectField
              {...field}
              label={t('audiences.basicInformationSection.gender')}
              placeholder={t('audiences.basicInformationSection.selectGender')}
              required={isFieldRequired(field.name)}
              error={error?.message}
              options={GENDER_OPTIONS}
            />
          )}
        />
        <Controller
          name="age"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaSelectField
              {...field}
              label={t('audiences.basicInformationSection.age')}
              placeholder={t('audiences.basicInformationSection.selectAge')}
              required={isFieldRequired(field.name)}
              error={error?.message}
              options={AGE_OPTIONS}
            />
          )}
        />
        <Controller
          name="profession"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaTextField
              {...field}
              label={t('audiences.basicInformationSection.profession')}
              placeholder={t('audiences.basicInformationSection.eGMarketingManager')}
              required={isFieldRequired(field.name)}
              error={error?.message}
            />
          )}
        />
      </div>

      <FormSubsectionV2>{t('audiences.basicInformationSection.location')}</FormSubsectionV2>

      <div className="grid grid-cols-[1fr_1fr_2fr] gap-sm">
        <Controller
          name="country"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaSelectField
              {...field}
              label={t('audiences.basicInformationSection.country')}
              placeholder={t('audiences.basicInformationSection.country')}
              required={isFieldRequired(field.name)}
              error={error?.message}
              options={countryOptions}
            />
          )}
        />
        <Controller
          name="state"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaTextField
              {...field}
              label={t('audiences.basicInformationSection.state')}
              placeholder={t('audiences.basicInformationSection.state')}
              required={isFieldRequired(field.name)}
              error={error?.message}
            />
          )}
        />
        <Controller
          name="city"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaTextField
              {...field}
              label={t('audiences.basicInformationSection.city')}
              placeholder={t('audiences.basicInformationSection.city')}
              required={isFieldRequired(field.name)}
              error={error?.message}
            />
          )}
        />
      </div>

      <FormSubsectionV2>{t('audiences.basicInformationSection.demographics')}</FormSubsectionV2>

      <div className="grid grid-cols-2 gap-sm">
        <Controller
          name="annualIncome"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaTextField
              {...field}
              onChange={(next) => field.onChange(normalizeDashes(next))}
              label={t('audiences.basicInformationSection.annualIncome')}
              placeholder={t('audiences.personaFieldsConstants.eG7500010000')}
              required={isFieldRequired(field.name)}
              error={error?.message}
            />
          )}
        />
        <Controller
          name="familyComposition"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <PersonaTextField
              {...field}
              label={t('audiences.basicInformationSection.familyComposition')}
              placeholder={t('audiences.basicInformationSection.eGMarriedWith2')}
              required={isFieldRequired(field.name)}
              error={error?.message}
            />
          )}
        />
      </div>

      <FormSubsectionV2>
        {t('audiences.basicInformationSection.communicationPreference')}
      </FormSubsectionV2>

      <Controller
        name="toneOfVoice"
        control={control}
        render={({ field, fieldState: { error } }) => (
          <PersonaTagField
            {...field}
            label={t('audiences.basicInformationSection.toneOfVoice')}
            placeholder={t('audiences.basicInformationSection.selectToneOrTypeTo')}
            required={isFieldRequired(field.name)}
            error={error?.message}
            options={TONE_OF_VOICE_OPTIONS}
          />
        )}
      />
    </FormSectionV2>
  );
}

BasicInformationSectionV2.displayName = 'BasicInformationSectionV2';
